import { useState, useContext } from 'react'
import { css } from '@emotion/react'
import PersonAddDisabledIcon from '@mui/icons-material/PersonAddDisabled'
import { CircularProgress, IconButton, TableCell, Tooltip } from '@mui/material'
import { useDispatch } from 'react-redux'
import { PendingStatus } from '@components'
import { modalContext } from '@contexts'
import { showSnackbar } from '@global/snackbar/snackbar.actions'
import { DeleteDatasetIcon } from '@submissions/presubmissions/delete-submission'
import { api } from 'api'
import { DeleteShareIcons } from './deleteShareIcons'
import { PendingRow } from '../types'
import { t } from '@lingui/core/macro'

const tableCell = css`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const icon = css`
  padding: 4px;
`

interface Props {
  row: PendingRow
  invalidateQueryCache: () => void
}

export const ActionButtons = ({ row, invalidateQueryCache }: Props) => {
  const { id, can_delete, type: rowType, status } = row
  const dispatch = useDispatch()
  const { displayModal } = useContext(modalContext)
  const [deleting, setDeleting] = useState({})

  const type = rowType || status // backend is returning two diff type references; globalPending vs pending rollup
  const isDeleting = Boolean(deleting[id])
  const isDraft = type === PendingStatus.DRAFT
  const isSharedDraft = type === PendingStatus.SHARED_DRAFT

  if (isDeleting) {
    return (
      <TableCell align="center" component="th" scope="row" padding="normal">
        <Tooltip title={t`Deleting`}>
          <CircularProgress size={16} />
        </Tooltip>
      </TableCell>
    )
  }

  if (isDraft) {
    return (
      <TableCell component="th" scope="row" padding="normal" css={tableCell}>
        <DeleteShareIcons draft={row} invalidateQueryCache={invalidateQueryCache} />
      </TableCell>
    )
  }

  if (isSharedDraft) {
    return (
      <TableCell component="th" scope="row" padding="normal" css={tableCell}>
        <IconButton
          css={icon}
          onClick={e => {
            unshareDraftModal(row, e, row.type)
          }}
          size="large"
        >
          <Tooltip title={t`Decline shared draft`}>
            <PersonAddDisabledIcon />
          </Tooltip>
        </IconButton>
      </TableCell>
    )
  }

  return (
    <TableCell component="th" scope="row" padding="normal">
      <DeleteDatasetIcon
        canDelete={can_delete}
        datasetId={id}
        invalidateQueryCache={invalidateQueryCache}
        setDeleting={setDeleting}
      />
    </TableCell>
  )

  function unshareDraftModal(draft, e, type) {
    const title = isSharedDraft
      ? 'Are you sure you want to decline this draft?'
      : 'Are you sure you want to delete?'

    const description = isSharedDraft
      ? "This action can't be undone. This draft will need to be reshared with you to for you to regain access."
      : "This action can't be undone and any work saved in this draft will be lost."

    displayModal({
      title,
      description,
      confirm: () => () => {
        unshareDraft(draft, type)
      },
    })
    e.stopPropagation()
  }

  function unshareDraft(draft, type) {
    const isSharedDraft = type === 'Shared Draft'

    // pending tab inside datasets uses dataset_id
    // global pending tab uses id
    const draft_id = draft.dataset_id || draft.id

    api
      .put('/api/v1/account/dataset/draft/unshareDraft', { draft_id })
      .then(() => {
        invalidateQueryCache()
        dispatch(
          showSnackbar({
            message: isSharedDraft ? 'Draft declined successfully' : 'Deleted successfully',
            type: 'SUCCESS',
            duration: 5000,
          }),
        )
      })
      .catch(() => {
        dispatch(
          showSnackbar({
            message: isSharedDraft ? "Draft couldn't be declined" : "Draft couldn't be deleted",
            type: 'ERROR',
            duration: 5000,
          }),
        )
      })
  }
}
